import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { PAYMENT } from "@/constants/testIds";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const POLL_MS = 2000;
const MAX_POLLS = 10;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling"); // polling | paid | pending | error
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let cancelled = false;
        let attempt = 0;

        const poll = async () => {
            if (cancelled) return;
            attempt += 1;
            setAttempts(attempt);
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (attempt >= MAX_POLLS) {
                    setStatus("pending");
                    return;
                }
                setTimeout(poll, POLL_MS);
            } catch {
                if (attempt >= MAX_POLLS) {
                    setStatus("error");
                    return;
                }
                setTimeout(poll, POLL_MS);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div>
            <Navbar />
            <main className="min-h-[80vh] flex items-center justify-center px-5 pt-32 pb-24">
                <div data-testid={PAYMENT.successCard} className="max-w-xl w-full bg-[#141414] border border-white/10 p-10 sm:p-14 text-center">
                    {status === "polling" && (
                        <>
                            <Loader2 className="w-14 h-14 mx-auto mb-6 text-brand animate-spin" />
                            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.03em] text-white">Confirming your payment...</h1>
                            <p data-testid={PAYMENT.statusText} className="mt-4 text-white/60">Just a moment while we sync with Stripe (attempt {attempts}/{MAX_POLLS}).</p>
                        </>
                    )}
                    {status === "paid" && (
                        <>
                            <CheckCircle2 className="w-14 h-14 mx-auto mb-6 text-brand" />
                            <div className="eyebrow mb-3">Payment received</div>
                            <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-[-0.04em] text-white">You're in.</h1>
                            <p data-testid={PAYMENT.statusText} className="mt-6 text-white/70 leading-relaxed">
                                Thank you! Babur will reach out on WhatsApp within 2 hours to share the onboarding form and calendar link.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                                <a
                                    href="https://wa.me/971543676278?text=Hi%20Babur!%20I%20just%20completed%20payment."
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 transition-colors"
                                >
                                    Open WhatsApp
                                </a>
                                <Link
                                    to="/"
                                    data-testid={PAYMENT.backHome}
                                    className="inline-flex items-center justify-center border border-white/20 hover:border-white/60 text-white font-semibold px-6 py-3 transition-colors"
                                >
                                    Back to home
                                </Link>
                            </div>
                        </>
                    )}
                    {status === "pending" && (
                        <>
                            <Loader2 className="w-14 h-14 mx-auto mb-6 text-brand" />
                            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.03em] text-white">Payment processing</h1>
                            <p data-testid={PAYMENT.statusText} className="mt-4 text-white/70">Your payment is still processing. We'll email you once confirmed. You can safely leave this page.</p>
                            <Link to="/" data-testid={PAYMENT.backHome} className="mt-8 inline-flex items-center justify-center border border-white/20 hover:border-white/60 text-white font-semibold px-6 py-3 transition-colors">
                                Back to home
                            </Link>
                        </>
                    )}
                    {status === "error" && (
                        <>
                            <XCircle className="w-14 h-14 mx-auto mb-6 text-red-400" />
                            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.03em] text-white">Something went off</h1>
                            <p data-testid={PAYMENT.statusText} className="mt-4 text-white/70">We couldn't confirm your payment. Please reach out on WhatsApp and we'll sort it in minutes.</p>
                            <a href="https://wa.me/971543676278" target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center justify-center bg-brand text-white font-semibold px-6 py-3">
                                Contact on WhatsApp
                            </a>
                        </>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
